"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  ErrorNotice,
  Field,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  INPUT_MONO,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  type Card,
  cardText,
  chanceOfAtLeastOne,
  classifyPokerHand,
  DECK_SIZE,
  deal,
  FIVE_CARD_HANDS,
  isRed,
  POKER_HAND_COUNTS,
  rankSymbol,
  SUIT_SYMBOL,
  shuffledDeck,
  targets,
} from "@/lib/tool-kit/random-fun/cards";

/** English strings. A translated content file overrides these through `ui`. */
export const RANDOM_CARD_DRAW_STRINGS = {
  jokersLabel: "Jokers in the deck",
  jokers0: "None (52 cards)",
  jokers1: "One joker (53 cards)",
  jokers2: "Two jokers (54 cards)",
  countLabel: "Cards to draw",
  freshLabel: "Shuffle a fresh deck before every draw",
  freshHint:
    "Untick it to deal from one shuffled deck without putting cards back.",
  draw: "Draw cards",
  shuffle: "Shuffle a new deck",
  results: "Your cards",
  placeholder: "Press Draw cards to deal from a shuffled deck.",
  joker: "Joker",
  cardName: "{rank} of {suit}",
  ranks: {
    "1": "Ace",
    "2": "Two",
    "3": "Three",
    "4": "Four",
    "5": "Five",
    "6": "Six",
    "7": "Seven",
    "8": "Eight",
    "9": "Nine",
    "10": "Ten",
    "11": "Jack",
    "12": "Queen",
    "13": "King",
  },
  suits: { S: "spades", H: "hearts", D: "diamonds", C: "clubs" },
  deckFresh: "A new deck of {size} cards — the first draw shuffles it.",
  deckLeft:
    "one: {count} card left in the deck|other: {count} cards left in the deck",
  deckShuffled: "Shuffled: {size} cards, all in random order.",
  announce: "Drew {list}.",
  copyHand: "Copy the cards",
  copied: "Copied!",
  problemCount: "Enter how many cards to draw, from 1 to {max}.",
  problemLeft:
    "Only {left} left in the deck. Shuffle a new deck or draw fewer cards.",
  pokerHeading: "Poker hand",
  pokerLine: "{hand}: {count} of {total} five-card hands, or {percent}.",
  pokerHands: {
    royalFlush: "Royal flush",
    straightFlush: "Straight flush",
    fourOfAKind: "Four of a kind",
    fullHouse: "Full house",
    flush: "Flush",
    straight: "Straight",
    threeOfAKind: "Three of a kind",
    twoPair: "Two pair",
    onePair: "One pair",
    highCard: "High card",
  },
  oddsHeading: "The odds for your draw",
  oddsCaption:
    "Chances for {n} {cardsWord} dealt from a full, shuffled deck of {size}",
  cardsWord: "one: card|other: cards",
  colQuestion: "Chance of drawing…",
  colInDeck: "In the deck",
  colOne: "On one card",
  colAtLeast: "At least one in {n}",
  targetSpecific: "One named card, such as the ace of spades",
  targetAce: "Any ace",
  targetSuit: "Any card of one suit",
  targetFace: "Any face card (J, Q, K)",
  targetRed: "Any red card",
  targetJoker: "A joker",
} as const;

type Strings = typeof RANDOM_CARD_DRAW_STRINGS;

const TARGET_KEY = {
  specific: "targetSpecific",
  ace: "targetAce",
  suit: "targetSuit",
  face: "targetFace",
  red: "targetRed",
  joker: "targetJoker",
} as const;

const POKER_SIZE = 5;

function cardName(card: Card, strings: Strings): string {
  if (card.kind === "joker") {
    return strings.joker;
  }
  return t(strings.cardName, {
    rank: strings.ranks[String(card.rank) as keyof Strings["ranks"]],
    suit: strings.suits[card.suit],
  });
}

function PlayingCard({ card, strings }: { card: Card; strings: Strings }) {
  const colour = isRed(card)
    ? "text-red-600 dark:text-red-400"
    : "text-foreground";
  return (
    <li
      className={`flex h-24 w-16 flex-col items-center justify-center rounded-lg border border-border bg-card font-semibold shadow-sm ${colour}`}
    >
      <span className="sr-only">{cardName(card, strings)}</span>
      {card.kind === "joker" ? (
        <span aria-hidden="true" className="text-xs">
          {strings.joker}
        </span>
      ) : (
        <>
          <span aria-hidden="true" className="text-2xl">
            {rankSymbol(card.rank)}
          </span>
          <span aria-hidden="true" className="text-2xl">
            {SUIT_SYMBOL[card.suit]}
          </span>
        </>
      )}
    </li>
  );
}

export default function RandomCardDrawWidget() {
  const strings = useToolUi(RANDOM_CARD_DRAW_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [jokers, setJokers] = useState("0");
  const [countText, setCountText] = useState("5");
  const [fresh, setFresh] = useState(true);
  const [deck, setDeck] = useState<Card[] | null>(null);
  const [hand, setHand] = useState<Card[]>([]);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [announcement, setAnnouncement] = useState("");

  const jokerCount = Number(jokers);
  const size = DECK_SIZE + jokerCount;
  const parsed = parseIntegerInput(countText);
  const oddsDraws = Math.min(size, Math.max(1, parsed ?? 1));
  const pct = {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  } as const;

  function reset(nextJokers: string) {
    setJokers(nextJokers);
    setDeck(null);
    setHand([]);
    setError("");
    setStatus("");
  }

  function shuffleNew() {
    setDeck(shuffledDeck(jokerCount));
    setHand([]);
    setError("");
    setStatus(t(strings.deckShuffled, { size: fmt(size) }));
  }

  function drawCards() {
    if (parsed === null || parsed < 1 || parsed > size) {
      setError(t(strings.problemCount, { max: fmt(size) }));
      return;
    }
    const source = fresh || deck === null ? shuffledDeck(jokerCount) : deck;
    if (parsed > source.length) {
      setError(t(strings.problemLeft, { left: fmt(source.length) }));
      return;
    }
    const { hand: dealt, rest } = deal(source, parsed);
    setError("");
    setHand(dealt);
    setDeck(fresh ? null : rest);
    setStatus(
      fresh
        ? ""
        : tPlural(strings.deckLeft, locale, rest.length, {
            count: fmt(rest.length),
          })
    );
    setAnnouncement(
      t(strings.announce, {
        list: dealt.map((card) => cardName(card, strings)).join(", "),
      })
    );
  }

  const poker = classifyPokerHand(hand);
  const deckLine =
    status || (deck === null ? t(strings.deckFresh, { size: fmt(size) }) : "");

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          id="rcd-jokers"
          label={strings.jokersLabel}
          onChange={reset}
          options={[
            { value: "0", label: strings.jokers0 },
            { value: "1", label: strings.jokers1 },
            { value: "2", label: strings.jokers2 },
          ]}
          value={jokers}
        />
        <Field id="rcd-count" label={strings.countLabel}>
          <input
            className={INPUT_MONO}
            id="rcd-count"
            inputMode="numeric"
            onChange={(event) => setCountText(event.target.value)}
            type="text"
            value={countText}
          />
        </Field>
        <div className="flex flex-col justify-end gap-1">
          <Checkbox
            checked={fresh}
            label={strings.freshLabel}
            onChange={(on) => {
              setFresh(on);
              setDeck(null);
              setStatus("");
            }}
          />
          <p className={HINT}>{strings.freshHint}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <button className={BUTTON_PRIMARY} onClick={drawCards} type="button">
          {strings.draw}
        </button>
        {fresh ? null : (
          <button
            className={BUTTON_SECONDARY}
            onClick={shuffleNew}
            type="button"
          >
            {strings.shuffle}
          </button>
        )}
      </div>
      {deckLine ? <p className={HINT}>{deckLine}</p> : null}
      {error ? <ErrorNotice>{error}</ErrorNotice> : null}

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <p aria-live="polite" className="sr-only">
          {announcement}
        </p>
        {hand.length === 0 ? (
          <Notice>{strings.placeholder}</Notice>
        ) : (
          <>
            <ul className="flex flex-wrap gap-2">
              {hand.map((card, index) => (
                <PlayingCard
                  card={card}
                  key={`${index}-${cardText(card, strings.joker)}`}
                  strings={strings}
                />
              ))}
            </ul>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyHand}
              text={hand.map((card) => cardText(card, strings.joker)).join(" ")}
            />
            {poker && hand.length === POKER_SIZE ? (
              <div className={RESULT_CARD}>
                <h3 className={WIDGET_H3}>{strings.pokerHeading}</h3>
                <p className="mt-1 text-foreground text-sm">
                  {t(strings.pokerLine, {
                    hand: strings.pokerHands[poker],
                    count: fmt(POKER_HAND_COUNTS[poker]),
                    total: fmt(FIVE_CARD_HANDS),
                    percent: fmt(POKER_HAND_COUNTS[poker] / FIVE_CARD_HANDS, {
                      style: "percent",
                      maximumSignificantDigits: 3,
                    }),
                  })}
                </p>
              </div>
            ) : null}
          </>
        )}
      </section>

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.oddsHeading}</h2>
        <DataTable
          caption={t(strings.oddsCaption, {
            n: fmt(oddsDraws),
            cardsWord: tPlural(strings.cardsWord, locale, oddsDraws),
            size: fmt(size),
          })}
          head={[
            strings.colQuestion,
            strings.colInDeck,
            strings.colOne,
            t(strings.colAtLeast, { n: fmt(oddsDraws) }),
          ]}
          rows={targets(jokerCount).map((target) => [
            strings[TARGET_KEY[target.id]],
            fmt(target.count),
            fmt(target.count / size, pct),
            fmt(chanceOfAtLeastOne(size, target.count, oddsDraws), pct),
          ])}
        />
      </section>
    </div>
  );
}
